import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from './BaseWidget';
import useWidgetData from './hooks/useWidgetData';
import useWidgetConfig from './hooks/useWidgetConfig';
import { transformWidgetData } from './config';
import WidgetEmptyState from './WidgetEmptyState';

/**
 * createWidget - Factory function for creating widget components
 * 
 * This factory reduces boilerplate by handling common widget patterns:
 * - Config/date/filter handling via useWidgetConfig
 * - Data fetching via useWidgetData
 * - Data transformation via config dataTransformers
 * - BaseWidget wrapper with loading/error states
 * - Optional empty state rendering
 * 
 * @param {string} widgetType - The widget type key (e.g., 'kpi', 'table', 'areachart')
 * @param {React.Component} InnerComponent - The component to render inside BaseWidget
 * @param {Object} options - Factory options
 * @param {boolean} options.includeDates - Whether to include dates in API requests (default: true)
 * @param {boolean} options.addDetailedResults - Whether to add add_detailed_results flag
 * @param {boolean} options.addTrendData - Whether to add add_trend_data flag
 * @param {Function} options.transformData - Custom transform function (default: uses transformWidgetData from config)
 * @param {Object} options.emptyState - Empty state configuration { title, description, icon }
 * @param {Function} options.getEmptyState - Function to compute empty state based on (config, data)
 * @param {string} options.containerClassName - Additional CSS classes for the container
 * @param {Object} options.containerStyle - Additional inline styles for the container
 * @returns {React.Component} - The wrapped widget component
 */
const createWidget = (widgetType, InnerComponent, options = {}) => {
  const { 
    includeDates = true,
    addDetailedResults = false,
    addTrendData = false,
    transformData = null,
    emptyState = null,
    getEmptyState = null,
    containerClassName = "h-full flex flex-col",
    containerStyle = { minHeight: '200px', height: '100%' }
  } = options;

  const Widget = ({ 
    widgetId, 
    config, 
    filters = [], 
    onRemove, 
    isEditMode = false,
    onFilterAdd 
  }) => {
    // Use centralized config hook for date/filter handling
    const { finalConfig, stableFilters } = useWidgetConfig(
      config, 
      filters, 
      { 
        includeDates,
        addDetailedResults,
        addTrendData
      }
    );
    
    // Fetch data using the configured settings
    const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);
    
    // Transform data using custom function or default schema transform
    const widgetData = useMemo(() => {
      if (!data) return null;
      
      if (transformData) {
        return transformData(data, config);
      }
      
      // Default transformer uses registry-based transform (doesn't need config)
      return transformWidgetData(widgetType, data);
      
    }, [data, config]);

    // Determine empty state content
    const emptyStateContent = useMemo(() => {
      if (getEmptyState) {
        return getEmptyState(config, widgetData);
      }
      return emptyState;
    }, [config, widgetData]);

    // Helper to determine if data is empty
    const isDataEmpty = (wd) => {
      if (!wd) return true;
      if (Array.isArray(wd.data)) return wd.data.length === 0;
      return wd.data === null || wd.data === undefined;
    };

    // Check if we should show empty state
    const showEmptyState = !isLoading && !error && isDataEmpty(widgetData);

    let content = null;
    if (widgetData && !showEmptyState) {
      content = (
        <div className="flex-1 flex flex-col h-full">
          <InnerComponent 
            data={widgetData}
            config={config}
            filters={stableFilters}
            onFilterAdd={onFilterAdd}
            isEditMode={isEditMode}
          />
        </div>
      );
    } else if (showEmptyState && emptyStateContent) {
      content = (
        <WidgetEmptyState 
          title={emptyStateContent.title}
          description={emptyStateContent.description}
          icon={emptyStateContent.icon}
        />
      );
    }

    return (
      <BaseWidget
        widgetId={widgetId}
        title={config.title}
        isLoading={isLoading}
        error={error}
        onRemove={onRemove}
        isEditMode={isEditMode}
      >
        <div className={containerClassName} style={containerStyle}>
          {content}
        </div>
      </BaseWidget>
    );
  };

  // Set display name for debugging
  const capitalizedType = widgetType.charAt(0).toUpperCase() + widgetType.slice(1);
  Widget.displayName = `${capitalizedType}Widget`;

  // Define PropTypes for the generated widget
  Widget.propTypes = {
    widgetId: PropTypes.string.isRequired,
    config: PropTypes.shape({
      title: PropTypes.string,
      dataSource: PropTypes.shape({
        type: PropTypes.oneOf(['static', 'api']).isRequired,
        data: PropTypes.any,
        endpoint: PropTypes.string,
        method: PropTypes.string,
        body: PropTypes.object,
        transform: PropTypes.func
      }).isRequired
    }).isRequired,
    filters: PropTypes.array,
    onRemove: PropTypes.func,
    isEditMode: PropTypes.bool,
    onFilterAdd: PropTypes.func
  };

  return Widget;
};

export default createWidget;
